import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type AuthUser = {
  id: string;
  email: string;
  displayName: string;
  jobTitle?: string | null;
  phone?: string | null;
  photoUrl?: string | null;
  mustChangePassword: boolean;
  roles: string[];
  permissions: string[];
};

export type MfaChallenge = {
  mfaRequired: true;
  setupRequired: boolean;
  challengeToken: string;
  expiresAt: string;
  secret?: string;
  otpauthUri?: string;
};

type AuthContextValue = {
  user?: AuthUser;
  loading: boolean;
  login: (email: string, password: string) => Promise<MfaChallenge | undefined>;
  verifyMfa: (challengeToken: string, code: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthUser>;
  updateProfile: (input: Pick<AuthUser, "displayName" | "email" | "jobTitle" | "phone">) => Promise<AuthUser>;
  updatePhoto: (file: File) => Promise<AuthUser>;
  can: (permission: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const IDLE_TIMEOUT_MS = 5 * 60_000;
const LAST_ACTIVITY_KEY = "sanru-rh-last-activity";

async function payload<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({ message: `Le serveur a refusé la requête (HTTP ${response.status}).` }));
  if (!response.ok) throw new Error((body as { message?: string }).message ?? "L’opération a échoué.");
  return body as T;
}

async function prepareProfilePhoto(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) throw new Error("Choisissez une image JPG ou PNG.");
  const bitmap = await createImageBitmap(file);
  try {
    const maximum = 1000;
    const scale = Math.min(1, maximum / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Votre navigateur ne peut pas préparer cette photo.");
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (!blob) throw new Error("La compression de la photo a échoué.");
    return new File([blob], `${file.name.replace(/\.[^.]+$/, "") || "photo"}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    bitmap.close();
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void fetch("/api/v1/auth/me")
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((body: { user: AuthUser }) => setUser(body.user))
      .catch(() => setUser(undefined))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    let timer = 0;
    let lastRecorded = 0;
    const schedule = () => {
      window.clearTimeout(timer);
      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || Date.now());
      const remaining = Math.max(250, IDLE_TIMEOUT_MS - (Date.now() - lastActivity));
      timer = window.setTimeout(async () => {
        const newestActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
        if (Date.now() - newestActivity < IDLE_TIMEOUT_MS) { schedule(); return; }
        await fetch("/api/v1/auth/logout", { method: "POST", keepalive: true }).catch(() => undefined);
        sessionStorage.removeItem("sanru-rh-login-message");
        localStorage.setItem("sanru-rh-idle-logout", String(Date.now()));
        setUser(undefined);
      }, remaining);
    };
    const activity = () => {
      const now = Date.now();
      if (now - lastRecorded < 5_000) return;
      lastRecorded = now; localStorage.setItem(LAST_ACTIVITY_KEY, String(now)); schedule();
    };
    const syncLogout = (event: StorageEvent) => { if (event.key === "sanru-rh-idle-logout") setUser(undefined); if (event.key === LAST_ACTIVITY_KEY) schedule(); };
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); schedule();
    const events: Array<keyof WindowEventMap> = ["pointerdown", "mousemove", "keydown", "wheel", "touchstart"];
    for (const event of events) window.addEventListener(event, activity, { passive: true });
    window.addEventListener("storage", syncLogout);
    return () => { window.clearTimeout(timer); for (const event of events) window.removeEventListener(event, activity); window.removeEventListener("storage", syncLogout); };
  }, [user?.id]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    login: async (email, password) => {
      const response = await fetch("/api/v1/auth/login", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }),
      });
      if (response.status === 202) return payload<MfaChallenge>(response);
      const body = await payload<{ user: AuthUser }>(response);
      setUser(body.user);
      return undefined;
    },
    verifyMfa: async (challengeToken, code) => {
      const body = await payload<{ user: AuthUser }>(await fetch("/api/v1/auth/mfa/verify", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challengeToken, code }),
      }));
      setUser(body.user);
      return body.user;
    },
    logout: async () => {
      await fetch("/api/v1/auth/logout", { method: "POST" });
      setUser(undefined);
    },
    changePassword: async (currentPassword, newPassword) => {
      const body = await payload<{ user: AuthUser }>(await fetch("/api/v1/auth/change-password", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ currentPassword, newPassword }),
      }));
      setUser(body.user);
      return body.user;
    },
    updateProfile: async (input) => {
      const body = await payload<{ user: AuthUser }>(await fetch("/api/v1/auth/profile", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) }));
      setUser(body.user);
      return body.user;
    },
    updatePhoto: async (file) => {
      const prepared = await prepareProfilePhoto(file);
      const form = new FormData(); form.append("file", prepared);
      const body = await payload<{ user: AuthUser }>(await fetch("/api/v1/auth/photo", { method: "POST", body: form }));
      setUser(body.user);
      return body.user;
    },
    can: (permission) => Boolean(user?.roles.includes("SUPER_ADMIN") || user?.permissions.includes(permission)),
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth doit être utilisé dans AuthProvider");
  return context;
}
